// Auction Watchlist — up to 5 favourited auctions. Purely client-side; the
// menu shows a reminder banner when any watched auction closes within 15 min.
const KEY = "ppe_watchlist_v1";
const NOTIF_KEY = "ppe_watchlist_notified_v1"; // { [auctionId]: 1 } to dedupe reminders
const MAX_WATCH = 5;
const REMIND_WINDOW_MS = 15 * 60 * 1000;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}
function write(v) {
  localStorage.setItem(KEY, JSON.stringify(v.slice(0, MAX_WATCH)));
}

export function getWatchlist() { return read(); }
export function isWatched(id) { return read().includes(id); }
export function watchedCount() { return read().length; }
export function maxWatch() { return MAX_WATCH; }

/** Push the current watchlist to the backend for push-alert routing. */
async function syncToBackend() {
  try {
    const { getEmail } = await import("@/lib/pro");
    const email = (getEmail() || "").trim();
    if (!email) return;
    const axios = (await import("axios")).default;
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/gems/watchlist-sync`, {
      email,
      auction_ids: read(),
    });
  } catch {}
}

/** Toggle a watchlist entry. Returns { ok, added?, removed?, reason? }. */
export function toggleWatch(id) {
  if (!id) return { ok: false, reason: "no id" };
  const list = read();
  const i = list.indexOf(id);
  if (i >= 0) {
    list.splice(i, 1);
    write(list);
    syncToBackend();
    return { ok: true, removed: true };
  }
  if (list.length >= MAX_WATCH) return { ok: false, reason: `Max ${MAX_WATCH} auctions can be watched` };
  list.push(id);
  write(list);
  syncToBackend();
  return { ok: true, added: true };
}

/** From a list of open auctions, return those on the watchlist expiring within 15 min. */
export function pendingReminders(auctions) {
  const list = read();
  if (list.length === 0 || !Array.isArray(auctions)) return [];
  const notified = readNotified();
  const now = Date.now();
  const out = [];
  for (const a of auctions) {
    if (!list.includes(a.id)) continue;
    if (notified[a.id]) continue;
    try {
      const exp = new Date(a.expires_at).getTime();
      const remaining = exp - now;
      if (remaining > 0 && remaining <= REMIND_WINDOW_MS) {
        out.push({ ...a, remainingMs: remaining });
      }
    } catch {}
  }
  return out;
}

function readNotified() {
  try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || "{}") || {}; }
  catch { return {}; }
}
export function markReminded(ids) {
  const n = readNotified();
  for (const id of ids) n[id] = 1;
  localStorage.setItem(NOTIF_KEY, JSON.stringify(n));
}

/** Clean up notified/watchlist entries for auctions that are gone from the open list. */
export function pruneWatchlist(openAuctionIds) {
  const set = new Set(openAuctionIds || []);
  const list = read().filter((id) => set.has(id));
  write(list);
  const n = readNotified();
  for (const id of Object.keys(n)) if (!set.has(id)) delete n[id];
  localStorage.setItem(NOTIF_KEY, JSON.stringify(n));
}
